import {useCallback, useContext, useEffect, useReducer, useRef} from "react";
import log from "loglevel";

import ManifestDetailsV2 from "./ManifestDetailsV2";
import {
  Driver,
  ManifestAttributeFilter,
  ManifestFilter,
  useSearchDriversLazyQuery,
  useSearchManifestDetailsLazyQuery
} from "../../../generated/graphql";
import {Address} from "../../../domain/geocode-types";
import {ManifestFilterState} from "../list/components/manifest-filter/ManifestFilterState";
import ManifestDetailsManifestSelector from "./ManifestDetailsManifestSelector";
import ManifestDetailsDriverSelector from "./ManifestDetailsDriverSelector";
import manifestDetailsReducer, {ManifestDetailsAssignmentState} from "./ManifestDetailsReducer";
import {ManifestDetailsChangeContext} from "./ManifestDetailsChangeEventProvider";
import {cloneDeep} from "lodash";
import {makeQueryStringFilter} from "../../../utils/FilterUtils";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../../providers/DispatchStationProvider";
import {useDispatchGroupContext} from "../../../providers/DispatchGroupProvider";
import {useAssignmentViewState} from "../../../providers/AssignmentViewProvider";
import {TManifestEnhanced} from "../../common/types";
import {buildManifestDriverGroupFilter} from "../../../utils/ManifestUtils";

type ManifestDetailsContainerProps = {
  manifestDriverId: number;
  onClose(): void;
  showMap?: boolean;
  isTestingEnvironment?: boolean;
};

type DetailViews = "NONE" | "MAP" | "ASSIGNDRIVER" | "SPLITJOB";

const InitialManifestDetailsState: ManifestDetailsAssignmentState = {
  selectedManifest: undefined,
  selectedManifestDriver: undefined,
  detailsViewType: "NONE"
};

const ManifestDetailsContainer = ({
  manifestDriverId,
  onClose,
  showMap = true,
  isTestingEnvironment = false
}: ManifestDetailsContainerProps) => {
  const [getDriver] = useSearchDriversLazyQuery({});
  const assignmentViewState = useAssignmentViewState();

  const [manifestDetailsAssignmentState, manifestDetailsAssignmentDispatch] = useReducer(manifestDetailsReducer, {
    ...InitialManifestDetailsState,
    detailsViewType: showMap ? "MAP" : "NONE",
    manifestId: Number(manifestDriverId),
    assignDriverManifestFilterState: assignmentViewState.manifestFilters
      ? assignmentViewState.manifestFilters.copy()
      : new ManifestFilterState()
  });

  const dispatchStationState = useDispatchStationContext();
  const {driverGroups, isDispatchGroupLoaded} = useDispatchGroupContext();
  const dataChangedState = useContext(ManifestDetailsChangeContext);
  const lastReFetch = useRef(0);

  log.debug("driverGroups:", driverGroups, isDispatchGroupLoaded);

  const [fetchManifest] = useSearchManifestDetailsLazyQuery({});

  const handleCloseManifestDetail = useCallback(() => {
    manifestDetailsAssignmentDispatch({type: "CLEAR_ALL_STATES"});
    onClose();
  }, [onClose]);

  const refreshManifest = useCallback(async () => {
    const result = await fetchManifest({
      variables: {filter: {manifest: {manifestDriverId: {eq: Number(manifestDriverId)}}}}
    });
    const manifest = result.data?.searchManifests?.items[0];
    if (manifest) {
      manifestDetailsAssignmentDispatch({
        type: "SET_SELECTED_MANIFEST",
        payload: cloneDeep(manifest) as TManifestEnhanced
      });
    }
  }, [fetchManifest, manifestDriverId]);

  // Single useEffect: initial load + event updates
  useEffect(() => {
    if (dataChangedState?.updated && dataChangedState.entityId === manifestDriverId.toString()) {
      if (dataChangedState.updated <= lastReFetch.current) return;
      lastReFetch.current = dataChangedState.updated + 500;
    }
    refreshManifest();
  }, [dataChangedState, manifestDriverId, refreshManifest]);

  useEffect(() => {
    const result = {
      manifest: {
        and: [] as ManifestAttributeFilter[]
      }
    } as ManifestFilter;

    result.manifest?.and?.push({
      or: [
        {manifestStatus: {match: "A"}},
        {manifestStatus: {match: "Q"}},
        {manifestStatus: {match: "I"}},
        {manifestStatus: {match: "F"}}
      ]
    });

    if (hasSelectedDispatchStations(dispatchStationState)) {
      result.manifest?.and?.push(buildManifestDriverGroupFilter(driverGroups));
    }

    manifestDetailsAssignmentState.assignDriverManifestFilterState?.appendToFilter(result);

    if (manifestDetailsAssignmentState.assignDriverManifestQuery) {
      result.queryString = makeQueryStringFilter(manifestDetailsAssignmentState.assignDriverManifestQuery, [
        "driver.name",
        "driver.code",
        "manifestDriverId",
        "stops.job.jobNumber",
        "stops.job.routeNumber"
      ]);
    }

    //log.debug("ManifestFilter: ", result);
    manifestDetailsAssignmentDispatch({type: "SET_ASSIGNDRIVER_MANIFEST_FILTER", payload: result});
  }, [
    manifestDetailsAssignmentState.assignDriverManifestFilterState,
    manifestDetailsAssignmentState.assignDriverManifestQuery,
    driverGroups,
    dispatchStationState
  ]);

  useEffect(() => {
    const loadDriver = async () => {
      const getDriverResult = await getDriver({
        variables: {
          filter: {
            driverId: {
              eq: manifestDetailsAssignmentState.selectedManifest?.driver?.driverId as number
            }
          }
        }
      });

      if (getDriverResult.data?.searchDrivers?.items) {
        const driver = getDriverResult.data?.searchDrivers?.items[0] as Driver;
        manifestDetailsAssignmentDispatch({
          type: "SET_SELECTED_MANIFEST_DRIVER",
          payload: driver
        });

        if (driver?.address && driver?.city && driver?.state && driver?.zip) {
          const homeAddress = new Address(driver?.address, driver?.city, driver?.state, driver?.zip);
          log.debug("Setting driver home address:", homeAddress);
          manifestDetailsAssignmentDispatch({
            type: "SET_MANIFEST_DRIVER_HOME_ADDRESS",
            payload: homeAddress
          });
        }
      }
    };

    if (manifestDetailsAssignmentState.selectedManifest) loadDriver().then(() => log.debug("Driver loaded"));
  }, [
    getDriver,
    manifestDetailsAssignmentState.selectedManifest?.driver?.driverId,
    manifestDetailsAssignmentState.selectedManifest
  ]);

  return (
    <ManifestDetailsV2
      manifestDriverId={manifestDetailsAssignmentState.manifestId ?? 0}
      manifestDetailsAssignmentState={manifestDetailsAssignmentState}
      manifestDetailsAssignmentDispatch={manifestDetailsAssignmentDispatch}
      onClose={handleCloseManifestDetail}
      detailViewType={manifestDetailsAssignmentState.detailsViewType}
      isTestingEnvironment={isTestingEnvironment}
      assignmentManifestList={
        <ManifestDetailsManifestSelector
          manifestDetailsAssignmentState={manifestDetailsAssignmentState}
          manifestDetailsAssignmentDispatch={manifestDetailsAssignmentDispatch}
        />
      }
      splitDriverList={
        <ManifestDetailsDriverSelector
          manifestDetailsAssignmentState={manifestDetailsAssignmentState}
          manifestDetailsAssignmentDispatch={manifestDetailsAssignmentDispatch}
        />
      }
    />
  );
};

export default ManifestDetailsContainer;
export type {ManifestDetailsContainerProps, DetailViews};
